// useActiveEmployees — employees who may be assigned as account manager.
//
// Returns rows from v_am_assignable, sorted by last then first name, mapped to
// { employee_id, name, title }. Used by the AM dropdown on the facility detail
// card (AFB-0025 part 1).
//
// ⚠️ THIS HOOK USED TO RETURN EVERY ACTIVE EMPLOYEE. It queried v_employees_am
// filtered only on employment_status='Active', so all 21 actives appeared in
// the AM picker — three Temporary Employee records, every Business Developer,
// HR, both finance leads. `employees.is_am_eligible` had been correct for
// months (7 true, 14 false) and nothing read it. The data fix shipped; the read
// path was never repointed. Fixed 2026-08-12, CW-L PR 3.
//
// v_am_assignable = Active AND (is_am_eligible OR already holds an
// Active/Onboarding facility). That second clause is a grandfather so nobody
// becomes unassignable by accident; `is_grandfathered` marks those rows.
// Writes are independently guarded by fn_is_am_assignable() — both a trigger on
// facilities.account_manager_eid and assign_facility_am() — so the next picker
// somebody builds cannot regress this.
//
// Do NOT widen this back to v_employees_am. That view is the full roster and 13
// other files legitimately need it (task assignee, info-request routing, name
// resolution); narrowing IT would break them. Two views, two jobs.
//
// The fetch is gated by `enabled` so surfaces that render the AM read-only
// (every non-superuser role) never fire the query.
//
// RLS note: public.employees is readable org-wide for superuser / AM /
// finance / hr / any TCS employee. The AM picker is superuser-gated at the
// call site, and the `superuser_all` policy lets superuser read the full
// active roster.
// ============================================================================

import { useEffect, useState } from 'react';
import { supabase } from '../../../shared/supabase';

export interface ActiveEmployeeOption {
  employee_id: string;
  name: string;
  title: string | null;
}

export function useActiveEmployees(enabled = true): {
  employees: ActiveEmployeeOption[];
  loading: boolean;
  error: string | null;
} {
  const [employees, setEmployees] = useState<ActiveEmployeeOption[]>([]);
  const [loading, setLoading] = useState(enabled);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!enabled) {
      setEmployees([]);
      setLoading(false);
      setError(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    (async () => {
      const { data, error: e } = await supabase
        .from('v_am_assignable')
        .select(
          'employee_id, employee_name_first, employee_name_last, employee_title',
        )
        .order('employee_name_last', { ascending: true })
        .order('employee_name_first', { ascending: true });
      if (cancelled) return;
      if (e) {
        setError(e.message);
        setEmployees([]);
        setLoading(false);
        return;
      }
      type Row = {
        employee_id: string;
        employee_name_first: string | null;
        employee_name_last: string | null;
        employee_title: string | null;
      };
      const mapped = ((data ?? []) as Row[]).map((r) => ({
        employee_id: r.employee_id,
        name:
          `${r.employee_name_first ?? ''} ${r.employee_name_last ?? ''}`.trim() ||
          r.employee_id,
        title: r.employee_title,
      }));
      setEmployees(mapped);
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [enabled]);

  return { employees, loading, error };
}
